import useCustomQueryVariables from "views/Settings/Accounting/components/Sales/SalesAccounts/hooks/useCustomQueryVariables";
import { createOptionCounterPartWithCycle } from "utils/createOptionCounterPartWithCycle";
import { LIST_PRODUCTS_TYPES_FOR_ASYNC_SELECT } from "queries/neoFact/catalog";
import { LIST_COUNTERPARTS_QUERY } from "queries/centralApi/counterparts";
import { clientCentralApiStrawberry, clientNeoFact } from "@neonumy/api-clients";
import { Card, Col, Divider, Form, Input, Row, Select } from "antd";
import LanguageTranslationTabs from "./LanguageTranslationTabs";
import { FaCreditCard, FaInfoCircle } from "react-icons/fa";
import { AsyncSelect } from "@neonumy/ant-design";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import CardTitle from "components/CardTitle";
import {
  LIST_UNIT_MEASURES_FOR_ASYNC_SELECT,
  LIST_VATS_FOR_ASYNC_SELECT,
} from "queries/neoFact/vats";

interface ProductFormFieldsProps {
  categoryOptions: any[];
  operationsCategories: any[];
  hasDeclareTva: boolean;
  counterpartByCategory: boolean;
  showProductId?: boolean;
  extraCategoryAction?: React.ReactNode;
}

export default function ProductFormFields({
  categoryOptions,
  operationsCategories,
  hasDeclareTva,
  counterpartByCategory,
  showProductId = false,
  extraCategoryAction,
}: ProductFormFieldsProps): React.ReactElement {
  const { t } = useTranslation();
  const urlParams: RouteParams = useParams();
  const customQueryVariables = useCustomQueryVariables();

  return (
    <div className="product-form-fields">
      {showProductId && (
        <Form.Item name="productId" hidden>
          <Input disabled />
        </Form.Item>
      )}

      <Row gutter={[0, 16]}>
        <Col span={24}>
          <Card
            size="small"
            title={<CardTitle icon={<FaInfoCircle />} title={t("General information")} />}
            className="antd-newrai-card"
          >
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="reference"
                  label={t("Reference")}
                  rules={[{ required: true, message: t("This field is required") }]}
                  className="form-item-compact"
                >
                  <Input placeholder={t("Enter reference")} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <div className="category-field-wrapper">
                  <Form.Item
                    name="category"
                    label={t("Categories")}
                    rules={[{ required: true, message: t("This field is required") }]}
                    className="form-item-compact"
                  >
                    <Select options={categoryOptions} placeholder={t("Select category")} />
                  </Form.Item>
                  {extraCategoryAction}
                </div>
              </Col>
            </Row>
            <Divider className="divider-compact" />
            <LanguageTranslationTabs />
          </Card>
        </Col>

        <Col span={24}>
          <Card
            size="small"
            title={<CardTitle icon={<FaCreditCard />} title={t("Pricing information")} />}
            className="antd-newrai-card"
          >
            <Row gutter={16}>
              <Col xs={24} sm={12} lg={6}>
                <Form.Item
                  name="productType"
                  label={t("Product type")}
                  rules={[{ required: true, message: t("This field is required") }]}
                  className="form-item-compact"
                >
                  <AsyncSelect
                    queryOptions={{
                      client: clientNeoFact,
                      dataName: "listProductTypes",
                      query: LIST_PRODUCTS_TYPES_FOR_ASYNC_SELECT,
                      variables: { search: "", companyId: urlParams?.companyId },
                    }}
                    translations={{
                      placeholder: t("Select type"),
                      loadingMessage: t("Loading"),
                      noOptionsMessage: t("No Options"),
                    }}
                    optionsFormat={item => ({
                      label: `${item.code} - ${item.name}`,
                      value: item.id,
                    })}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Form.Item
                  name="unitMeasure"
                  label={t("Unit measure")}
                  rules={[{ required: true, message: t("This field is required") }]}
                  className="form-item-compact"
                >
                  <AsyncSelect
                    queryOptions={{
                      client: clientNeoFact,
                      dataName: "listUnitMeasures",
                      query: LIST_UNIT_MEASURES_FOR_ASYNC_SELECT,
                      variables: { search: "", companyId_In: [urlParams?.companyId] },
                    }}
                    translations={{
                      placeholder: t("Select unit"),
                      loadingMessage: t("Loading"),
                      noOptionsMessage: t("No Options"),
                    }}
                    optionsFormat={item => ({
                      label: `${item.slug} - ${item?.name}`,
                      value: item.id,
                    })}
                  />
                </Form.Item>
              </Col>

              {hasDeclareTva && (
                <Col xs={24} sm={12} lg={6}>
                  <Form.Item
                    name="vat"
                    label={t("VAT rate")}
                    rules={[{ required: true, message: t("This field is required") }]}
                    className="form-item-compact"
                  >
                    <AsyncSelect
                      queryOptions={{
                        client: clientNeoFact,
                        dataName: "listVats",
                        query: LIST_VATS_FOR_ASYNC_SELECT,
                        variables: {
                          search: "",
                          companyId: urlParams?.companyId,
                          activeOnly: true,
                        },
                      }}
                      translations={{
                        placeholder: t("Select VAT"),
                        loadingMessage: t("Loading"),
                        noOptionsMessage: t("No Options"),
                      }}
                      optionsFormat={item => ({
                        label: `${item.code} - ${item.name} - ${item?.ratePercentage} %`,
                        value: item.id,
                        rate: item?.rate,
                      })}
                    />
                  </Form.Item>
                </Col>
              )}

              <Col xs={24} sm={12} lg={6}>
                <Form.Item
                  name="operationalCategory"
                  label={t("Operational category")}
                  rules={[{ required: true, message: t("This field is required") }]}
                  className="form-item-compact"
                >
                  <Select options={operationsCategories} placeholder={t("Select category")} />
                </Form.Item>
              </Col>
            </Row>

            {counterpartByCategory && (
              <>
                <Divider className="divider-counterpart" />
                <Form.Item
                  name="counterpartCode"
                  label={t("Counterpart product")}
                  rules={[{ required: true, message: t("This field is required") }]}
                  className="form-item-no-margin"
                >
                  <AsyncSelect
                    optionsFormat={createOptionCounterPartWithCycle}
                    customQueryVariables={customQueryVariables}
                    hasQueryFilter={true}
                    queryOptions={{
                      client: clientCentralApiStrawberry,
                      dataName: "listCounterpartsPaginated",
                      query: LIST_COUNTERPARTS_QUERY,
                      variables: {
                        filter: {
                          companyId: urlParams.companyId,
                          isActive: true,
                          search: "",
                          code_Istartswith_In: ["7"],
                        },
                        limit: 100,
                      },
                    }}
                    translations={{
                      placeholder: t("Select counterpart"),
                      loadingMessage: t("Loading..."),
                      noOptionsMessage: t("No Options"),
                    }}
                  />
                </Form.Item>
              </>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
